import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as TourCostPricesService from '~/services/TourCostPricesService';

import { CostPriceUploadBody, ServiceIdentifier } from '~/types/tours/costPrice';

export const costPriceQueryKeys = {
  all: ['tour-cost-prices'] as const,
  services: () => [...costPriceQueryKeys.all, 'service'] as const,
  service: (service: Partial<ServiceIdentifier>) =>
    [...costPriceQueryKeys.services(), service.supplierId ?? '', service.serviceId ?? ''] as const,
  tourServices: (tourId: string | undefined) => [...costPriceQueryKeys.all, 'tour-services', tourId ?? ''] as const,
};

export function useGetTourServices(tourId: string | undefined) {
  return useQuery({
    queryKey: costPriceQueryKeys.tourServices(tourId),
    queryFn: ({ signal }) => TourCostPricesService.getTourServices(tourId, { signal }),
    enabled: !!tourId,
  });
}

/**
 * svc-tour keys cost prices by supplier service, so both ids are required
 * before the query can run.
 */
export function useGetServiceCostPrices(service: Partial<ServiceIdentifier>) {
  const { supplierId, serviceId } = service;

  return useQuery({
    queryKey: costPriceQueryKeys.service(service),
    queryFn: ({ signal }) => TourCostPricesService.getServiceCostPrices({ supplierId, serviceId }, { signal }),
    enabled: !!supplierId && !!serviceId,
  });
}

export function useUploadCostPrices(service: ServiceIdentifier) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CostPriceUploadBody) => TourCostPricesService.uploadCostPrices(service, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: costPriceQueryKeys.service(service) });
    },
  });
}
